import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { FaFileAlt, FaCheckCircle, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import { createReport, listReports, listCampaignsApi, getAgents } from '../api/api';
import { Select } from '../components/Select';
import { InlineSpinner, TableRowsSkeleton } from '../components/LoadingState';

const PRESETS = [
  { label: 'Last 5 Days', days: 5 },
  { label: 'Last 7 Days', days: 7 },
  { label: 'Last 30 Days', days: 30 },
];

// Generation-time Focus is an inclusion filter only (assessed/unassessed) — it
// changes which calls actually go into the report, the same way campaign/scorecard
// do. The Top/Bottom 20 by QA/Compliance Score ranks are a viewing lens on a report
// that already exists, not a reason to generate a whole separate one, so they live
// exclusively in ReportDetail.js's preview-only Focus switcher instead.
const RANK_OPTIONS = [
  { value: 'assessed', label: 'Assessed calls only' },
  { value: 'unassessed', label: 'Unassessed calls only' },
];

// Sales / non-sales scoping. A separate control from Focus rather than two more entries in
// it: the two are independent (a call can be assessed and non-sales), and Focus is a single
// choice, so sharing it would make "assessed sales calls" impossible to ask for. Applied in
// the report's own query (see pca-exec-summary lib/common.js buildSalesCondition), so it
// scopes the grid, both exports and both PDFs alike. Values and labels must match
// SALES_FILTERS / SALES_FILTER_LABELS there — the backend refuses anything else.
const SALES_OPTIONS = [
  { value: 'sales', label: 'Sales calls only' },
  { value: 'non_sales', label: 'Non-sales calls only' },
];

// Which date the From/To range is matched against. Both count whole SAST days. Call date is
// the original behaviour; Processed date matches what Search shows.
const DATE_BASIS_OPTIONS = [
  { value: 'call', label: 'Call date' },
  { value: 'processed', label: 'Processed date' },
];

// Defaults to yesterday only — a small, fast first report. Widen it with the
// presets or the date inputs themselves; nothing here restricts how far a report
// can actually reach.
function defaultRange() {
  const yesterday = DateTime.now().minus({ days: 1 }).toISODate();
  return { dateFrom: yesterday, dateTo: yesterday };
}

// XLSX/CSV each fail independently of the grid (and of each other) — see
// ReportDetail.js's header comment for why. A failed export never turns the whole
// report Failed here; it appends its own note onto an otherwise-Complete badge, so
// "something needs a Retry" is visible from the list without hiding that the report
// itself (the grid preview) came through fine.
function exportFailureNote(report) {
  const failed = [];
  if (report.xlsxStatus === 'failed') failed.push('XLSX');
  if (report.csvStatus === 'failed') failed.push('CSV');
  if (!failed.length) return null;
  return `${failed.join(' & ')} Failed`;
}

function getStatusBadge(report) {
  switch (report.status) {
    case 'complete': {
      const note = exportFailureNote(report);
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <FaCheckCircle className="mr-1" />
          {note ? `Complete, ${note}` : 'Complete'}
        </span>
      );
    }
    case 'failed':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <FaExclamationTriangle className="mr-1" />
          Failed
        </span>
      );
    case 'processing':
    default:
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <FaClock className="mr-1" />
          Processing
        </span>
      );
  }
}

function formatDate(timestamp) {
  return new Date(timestamp).toLocaleString();
}

const Reports = ({ setAlert }) => {
  const navigate = useNavigate();

  const [dateFrom, setDateFrom] = useState(defaultRange().dateFrom);
  const [dateTo, setDateTo] = useState(defaultRange().dateTo);
  const [campaignFilter, setCampaignFilter] = useState([]);
  const [scorecardFilter, setScorecardFilter] = useState([]);
  const [agentFilter, setAgentFilter] = useState([]);
  const [guidFilter, setGuidFilter] = useState('');
  const [rankByFilter, setRankByFilter] = useState('');
  const [salesFilter, setSalesFilter] = useState('');
  const [dateBasis, setDateBasis] = useState('call');
  const [isCreating, setIsCreating] = useState(false);

  // Raw campaigns response (campaignName + its own templates), not just a flat name
  // list — scorecardOptions below is derived from this so it can stay aligned with
  // whichever campaigns are currently selected, instead of a flat union of every
  // scorecard across every campaign regardless of the Campaign filter.
  const [campaigns, setCampaigns] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);

  const [reports, setReports] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  // True when the exec-summary API itself is unreachable on this environment
  // (routes absent), as opposed to a transient failure — drives the adapted UI.
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Campaign/scorecard/agent options come from pca-ui's own existing endpoints
  // (already used by Campaign Management and Search) instead of an Athena DISTINCT
  // query — that query was itself at risk of the same 29s-timeout class of bug this
  // whole rework exists to fix, and this data is already the authoritative source
  // for "what campaigns/agents exist" anyway. GUID is a free-text filter rather than
  // a dropdown of every distinct value for the same reason — that was the highest-
  // cardinality, slowest part of the old query.
  const loadFilterOptions = useCallback(async () => {
    try {
      const [campaignsRes, agentsRes] = await Promise.all([listCampaignsApi(), getAgents()]);
      const campaignsList = campaignsRes.campaigns || [];
      setCampaigns(campaignsList);
      setCampaignOptions(campaignsList.map((c) => c.campaignName).filter(Boolean).sort());
      setAgentOptions(Array.isArray(agentsRes) ? agentsRes : []);
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  }, []);

  // Scorecards belonging to the selected campaign(s) only — with nothing selected,
  // falls back to every scorecard across every campaign (today's original behavior).
  const scorecardOptions = useMemo(() => {
    const relevant = campaignFilter.length
      ? campaigns.filter((c) => campaignFilter.includes(c.campaignName))
      : campaigns;
    return [...new Set(relevant.flatMap((c) => c.templates || []))].filter(Boolean).sort();
  }, [campaigns, campaignFilter]);

  // Drops any already-selected scorecard that no longer belongs to the current
  // campaign selection, so the form can never submit a Campaign/Scorecard
  // combination that doesn't actually exist together.
  useEffect(() => {
    setScorecardFilter((prev) => prev.filter((s) => scorecardOptions.includes(s)));
  }, [scorecardOptions]);

  const loadReports = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setServiceUnavailable(false);
    try {
      const data = await listReports();
      setReports(data.reports || []);
      setNextToken(data.nextToken || null);
      setHasLoaded(true);
    } catch (err) {
      console.error('Error loading reports:', err);
      // Adapt to whatever backend is actually deployed: a CORS/network failure
      // or a 403/404 on this route means the exec-summary API isn't present on
      // this environment (its routes get wiped whenever the pca-ui stack
      // deploys — see pca-exec-summary/cfn/lib/exec-summary-api.template).
      // That's a different situation from a transient server error, and the
      // page says so instead of a generic failure.
      const msg = String(err?.message || '');
      if (msg === 'Network error' || msg.startsWith('HTTP 403') || msg.startsWith('HTTP 404')) {
        setServiceUnavailable(true);
      } else {
        setError(err);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!nextToken || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const data = await listReports({ nextToken });
      setReports((prev) => [...prev, ...(data.reports || [])]);
      setNextToken(data.nextToken || null);
    } catch (err) {
      console.error('Error loading more reports:', err);
      setAlert && setAlert({ variant: 'error', heading: 'Error', text: 'Failed to load more reports.' });
    } finally {
      setIsLoadingMore(false);
    }
  }, [nextToken, isLoadingMore, setAlert]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    if (!hasLoaded) loadReports();
  }, [hasLoaded, loadReports]);

  const applyPreset = (days) => {
    setDateFrom(DateTime.now().minus({ days }).toISODate());
    setDateTo(DateTime.now().toISODate());
  };

  // Navigates straight to the new report's detail page (still Processing there) —
  // matches inspecting a freshly-created Job right after creating it, rather than
  // leaving the user to find it again in the list below.
  // Campaign and Scorecard are required, not optional narrowing — an unfiltered
  // report can blend every scorecard active in the date range into one pivot, and
  // the grid preview's 10-call cap only bounds rows, not the resulting column count
  // (one per distinct question across every blended scorecard). Wide enough, that
  // already exceeds Lambda's 6MB synchronous response ceiling in production. The
  // backend enforces this too (handleReportsCreate) — this is just the fast,
  // same-message path so the click never round-trips for something that will 422.
  const canGenerate = campaignFilter.length > 0 && scorecardFilter.length > 0;

  const handleGenerate = async () => {
    if (!dateFrom || !dateTo) {
      setAlert && setAlert({ variant: 'warning', heading: 'Date range required', text: 'Select a start and end date.' });
      return;
    }
    if (dateFrom > dateTo) {
      setAlert && setAlert({ variant: 'warning', heading: 'Invalid date range', text: 'Start date must be before end date.' });
      return;
    }
    if (!canGenerate) {
      setAlert && setAlert({ variant: 'warning', heading: 'Campaign and Scorecard required', text: 'Select at least one campaign and one scorecard before generating a report.' });
      return;
    }

    setIsCreating(true);
    try {
      const params = { dateFrom, dateTo, dateBasis };
      if (campaignFilter.length) params.campaign = campaignFilter.join(',');
      if (scorecardFilter.length) params.scorecard = scorecardFilter.join(',');
      if (agentFilter.length) params.agent = agentFilter.join(',');
      if (guidFilter) params.guid = guidFilter;
      if (salesFilter) params.salesFilter = salesFilter;
      if (rankByFilter) params.rankBy = rankByFilter;

      const res = await createReport(params);
      if (!res.reportId) {
        throw new Error('No report id returned');
      }
      navigate(`/reports/${res.reportId}`);
    } catch (err) {
      console.error('Failed to create report:', err);
      setAlert && setAlert({ variant: 'error', heading: 'Error', text: 'Failed to start report generation. Please try again.' });
    } finally {
      setIsCreating(false);
    }
  };

  const isPresetActive = (days) =>
    dateFrom === DateTime.now().minus({ days }).toISODate() && dateTo === DateTime.now().toISODate();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">QA Reports</h1>
        <p className="text-gray-500 text-sm mt-1">
          Generate a per-call, per-question QA scorecard report sourced from the Athena analytics view.
        </p>
      </div>

      <div className="card p-6 space-y-6" data-tour="report-filters">
        <div>
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">Date Range</p>
          <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Quick select</label>
              <div className="inline-flex h-[42px] items-center rounded-full border border-gray-200 p-0.5 bg-gray-50">
                {PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => applyPreset(p.days)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                      isPresetActive(p.days)
                        ? 'bg-cloudza-600 text-white shadow-sm'
                        : 'text-gray-500 hover:text-gray-800'
                    }`}
                    aria-pressed={isPresetActive(p.days)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">From</label>
              <input
                type="date"
                className="input-field w-44 !rounded-full !bg-gray-50 hover:!bg-gray-100 !border-gray-200"
                value={dateFrom}
                max={dateTo}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">To</label>
              <input
                type="date"
                className="input-field w-44 !rounded-full !bg-gray-50 hover:!bg-gray-100 !border-gray-200"
                value={dateTo}
                min={dateFrom}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Days run 00:00–23:59 SAST. Processed date matches Search.</p>
        </div>

        <div className="pt-6 border-t border-gray-100">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">Filters</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Campaign *</label>
              <Select
                isMulti
                options={campaignOptions.map((c) => ({ value: c, label: c }))}
                value={campaignFilter.map((c) => ({ value: c, label: c }))}
                onChange={(opts) => setCampaignFilter(opts ? opts.map((o) => o.value) : [])}
                placeholder="Select at least one campaign"
                isClearable
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Scorecard *</label>
              <Select
                isMulti
                options={scorecardOptions.map((s) => ({ value: s, label: s }))}
                value={scorecardFilter.map((s) => ({ value: s, label: s }))}
                onChange={(opts) => setScorecardFilter(opts ? opts.map((o) => o.value) : [])}
                placeholder="Select at least one scorecard"
                isClearable
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Agent</label>
              <Select
                isMulti
                options={agentOptions.map((a) => ({ value: a, label: a }))}
                value={agentFilter.map((a) => ({ value: a, label: a }))}
                onChange={(opts) => setAgentFilter(opts ? opts.map((o) => o.value) : [])}
                placeholder="All agents"
                isClearable
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">GUID</label>
              <input
                type="text"
                className="input-field"
                value={guidFilter}
                onChange={(e) => setGuidFilter(e.target.value)}
                placeholder="Search by GUID..."
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Focus</label>
              <Select
                options={RANK_OPTIONS}
                value={RANK_OPTIONS.find((o) => o.value === rankByFilter) || null}
                onChange={(opt) => setRankByFilter(opt ? opt.value : '')}
                placeholder="All calls"
                isClearable
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Sales Status</label>
              <Select
                options={SALES_OPTIONS}
                value={SALES_OPTIONS.find((o) => o.value === salesFilter) || null}
                onChange={(opt) => setSalesFilter(opt ? opt.value : '')}
                placeholder="All calls"
                isClearable
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Dates by</label>
              <Select
                options={DATE_BASIS_OPTIONS}
                value={DATE_BASIS_OPTIONS.find((o) => o.value === dateBasis)}
                onChange={(opt) => setDateBasis(opt.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-6 border-t border-gray-100">
          <button className="btn-primary" onClick={handleGenerate} disabled={isCreating || !canGenerate || serviceUnavailable}>
            {isCreating ? <><InlineSpinner light /> Generating…</> : 'Generate Report'}
          </button>
          {serviceUnavailable ? (
            <p className="text-xs text-amber-700">
              The reporting service isn't reachable on this environment right now, so new reports can't be generated.
            </p>
          ) : !canGenerate && (
            <p className="text-xs text-gray-500">Select at least one campaign and one scorecard to generate a report.</p>
          )}
        </div>
      </div>

      <div className="card !p-0 overflow-hidden">
        <div className="px-6 pt-5 pb-4">
          <h2 className="text-lg font-bold text-gray-900 tracking-tight">Past Reports</h2>
        </div>
        {serviceUnavailable ? (
          <div className="px-6 pb-6">
            <div className="p-4 rounded-lg border bg-amber-50 border-amber-200">
              <p className="text-sm font-medium text-amber-800">Reporting is unavailable on this environment.</p>
              <p className="text-xs text-amber-700 mt-1">
                The exec-summary API isn't reachable — its routes are missing or the service isn't
                deployed here. Past reports and report generation will come back as soon as it is
                restored; nothing has been lost.
              </p>
            </div>
            <button className="btn-outline mt-3" onClick={loadReports}>Check again</button>
          </div>
        ) : error ? (
          <div className="px-6 pb-6">
            <p className="text-sm text-red-700">Failed to load reports. Please try again.</p>
            <button className="btn-outline mt-3" onClick={loadReports}>Retry</button>
          </div>
        ) : !isLoading && reports.length === 0 ? (
          <div className="text-center py-14">
            <FaFileAlt className="mx-auto h-8 w-8 text-gray-300 mb-3" />
            <p className="text-sm font-medium text-gray-600">No reports generated yet</p>
            <p className="text-xs text-gray-400 mt-1">Pick a date range and filters above, then Generate Report.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#fafafc]">
                <tr>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Date Range</th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Campaign</th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Scorecard</th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {isLoading ? (
                  <TableRowsSkeleton columns={6} rows={4} />
                ) : reports.map((report) => (
                  <tr
                    key={report.reportId}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/reports/${report.reportId}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {report.dateFrom} to {report.dateTo}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{report.campaign || 'All'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{report.scorecard || 'All'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(report.createdAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(report)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-cloudza-600 hover:text-cloudza-800">View →</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {nextToken && (
              <div className="px-6 py-4 border-t border-gray-100 text-center">
                <button
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="btn-outline"
                >
                  {isLoadingMore ? <><InlineSpinner /> Loading...</> : 'Load More Reports'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
